import { GetOrderNotificationRes_Product } from "gen/api/order/service_pb"
import { Tier } from "gen/price/models_pb"
import {
  Product as PBProduct,
  Product_TaxStatus as ProductTaxStatus,
} from "gen/product/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { SearchSalesV2Res_Result_ReturnedInSales as ReturnedInSales } from "gen/search/service/service_pb"
import { TintColor as PBTintColor } from "gen/tintcolor/models_pb"
import {
  Order_Product as OrderProduct,
  Sale,
  Transfer_Product as TransferProduct,
} from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import { hasValue } from "shared-ts/utils/nullish/nullish"

import OrderForm from "features/pos/purchase-orders/types/OrderForm"
import { convertSaleToSaleLink } from "features/pos/sales/components"
import { signedQuantity } from "features/pos/sales/helpers/saleProduct"
import { Discount } from "features/pos/sales/salesSlice"
import { SaleProduct } from "features/pos/sales/types"
import { SaleLink } from "features/pos/sales/types/SaleForm.types"
import { SaleRowProduct } from "features/pos/sales/types/SaleProductRow.types"
import { stringify } from "lib/stringify"

import { TransactionProduct } from "../transactionProduct"

export const fixTransferProductsOrders = (
  products: Record<string, TransferProduct>
) => {
  const productsList = Object.values(products)
  const updatedProductsList = productsList
    .sort((a, b) => a.position - b.position)
    .map((product, idx) => ({
      ...product,
      position: idx + 1, // 1-based position
    }))

  const updatedProducts: Record<string, TransferProduct> = {}
  for (const updatedProduct of updatedProductsList) {
    if (!updatedProduct.productId) {
      continue
    }
    const id = UUID.fromPB(updatedProduct.productId).toString()
    updatedProducts[id] = updatedProduct
  }

  return updatedProducts
}

export const fixTxnProductsListOrder = (
  products: { position: number; productId?: PBUUID }[]
) =>
  [...products]
    .sort((a, b) => a.position - b.position)
    .map((product, idx) => ({
      ...product,
      position: idx + 1, // 1-based position
    }))

// Update the order form with the newly added product data.
export const updateOrderFormWithProductData = (
  state: OrderForm,
  newlyAddedProduct: TransactionProduct | GetOrderNotificationRes_Product
): OrderForm => {
  state.products = fixTxnProductsListOrder(
    state.products ?? []
  ) as OrderProduct[]

  if (!newlyAddedProduct.product?.id) {
    return state
  }

  state.productsById = {
    ...state.productsById,
    [UUID.fromPB(newlyAddedProduct.product.id).toString()]:
      newlyAddedProduct.product,
  }

  if (newlyAddedProduct.matchedCostProduct?.id) {
    state.productsById = {
      ...state.productsById,
      [UUID.fromPB(newlyAddedProduct.matchedCostProduct.id).toString()]:
        newlyAddedProduct.matchedCostProduct,
    }
  }

  if (newlyAddedProduct.matchedPriceProduct?.id) {
    state.productsById = {
      ...state.productsById,
      [UUID.fromPB(newlyAddedProduct.matchedPriceProduct.id).toString()]:
        newlyAddedProduct.matchedPriceProduct,
    }
  }

  if (newlyAddedProduct.catalogProduct) {
    state.catalogProductsById = {
      ...state.catalogProductsById,
      [UUID.fromPB(newlyAddedProduct.product.id).toString()]:
        newlyAddedProduct.catalogProduct,
    }
  }

  if (newlyAddedProduct.inventories) {
    state.inventoriesByProductId = {
      ...state.inventoriesByProductId,
      [UUID.fromPB(newlyAddedProduct.product.id).toString()]:
        newlyAddedProduct.inventories,
    }
  }

  if (newlyAddedProduct.additionalFees?.length) {
    state.additionalFeesByProductId = {
      ...state.additionalFeesByProductId,
      [UUID.fromPB(newlyAddedProduct.product.id).toString()]:
        newlyAddedProduct.additionalFees,
    }
  }

  // Always set tierPricesByProductId entry (even if empty) to avoid undefined
  // errors when the component tries to access it. Some code paths (like catalog
  // import) may not have tier prices populated yet.
  state.tierPricesByProductId = {
    ...state.tierPricesByProductId,
    [UUID.fromPB(newlyAddedProduct.product.id).toString()]:
      newlyAddedProduct.tierPrices ?? [],
  }

  return state
}

export const checkProductsEquality = (
  productsA: { productId?: PBUUID }[],
  productsB: { productId?: PBUUID }[]
) => {
  if (productsA.length !== productsB.length) {
    return false
  }
  productsA.sort((pa, pb) => UUID.comparePB(pa.productId, pb.productId))
  productsB.sort((pa, pb) => UUID.comparePB(pa.productId, pb.productId))
  return stringify(productsA) === stringify(productsB)
}

export const checkProductArrayEquality = (
  productsA: SaleProduct[],
  productsB: SaleProduct[]
) => {
  if (productsA.length !== productsB.length) {
    return false
  }

  return stringify(productsA) === stringify(productsB)
}

export function getProductUnitPriceV2(
  tierPrices: Tier[],
  pricingTierIdString: string
): Money {
  return (
    Money.fromPB(
      tierPrices.find((tier: Tier) => tier.tierIdString === pricingTierIdString)
        ?.price
    ) ?? Money.zero()
  )
}

export function getSaleRowProductsFromMap(
  saleProducts: SaleProduct[] = [],
  productsById: { [key: string]: PBProduct } = {},
  tintColorsById: { [key: string]: PBTintColor } = {},
  returnedFromSalesBySaleId: { [key: string]: Sale } = {},
  returnedToSalesBySaleProductId: {
    [key: string]: ReturnedInSales
  } = {}
): {
  products: SaleRowProduct[]
  quantityString: string
} {
  const products: SaleRowProduct[] = [...saleProducts]
    .sort((a, b) => a.position - b.position)
    .filter(hasValue("id"))
    .map((saleProduct) => {
      const fullProduct: PBProduct | undefined = saleProduct.productId
        ? productsById[UUID.fromPB(saleProduct.productId).toString()]
        : undefined

      const saleProductIdString = UUID.idString(saleProduct.id)

      const returnedFromSale =
        saleProduct.returnedFromSaleId &&
        returnedFromSalesBySaleId[UUID.idString(saleProduct.returnedFromSaleId)]

      const returnedInSales =
        returnedToSalesBySaleProductId[saleProductIdString]

      const linkedSales: SaleLink[] = []
      if (returnedFromSale) {
        linkedSales.push(convertSaleToSaleLink(returnedFromSale))
      } else if (returnedInSales) {
        linkedSales.push(
          ...(returnedInSales?.sales?.map((sale) =>
            convertSaleToSaleLink(sale)
          ) ?? [])
        )
      }

      const srp: SaleRowProduct = {
        id: UUID.fromPB(saleProduct.id),
        identifier: fullProduct?.identifiers[0] ?? "",
        name: fullProduct?.name ?? "",
        price: Money.fromPB(saleProduct?.price),
        tint: saleProduct.tintColorId
          ? tintColorsById[UUID.fromPB(saleProduct.tintColorId).toString()]
          : null,
        customTint: saleProduct.customTintColor ?? "",
        isTaxed: saleProduct.isTaxed,
        taxStatus: fullProduct?.taxStatus ?? ProductTaxStatus.UNDEFINED,
        returnedFromSaleId: saleProduct.returnedFromSaleId
          ? UUID.fromPB(saleProduct.returnedFromSaleId)
          : undefined,
        returnedFromSaleProductId: saleProduct.returnedFromSaleProductId
          ? UUID.fromPB(saleProduct.returnedFromSaleProductId)
          : undefined,
        type: saleProduct.type,
        quantity: signedQuantity(saleProduct),
        notes: saleProduct.notes,
        internalNotes: saleProduct.internalNotes,
        linkedSales,
        giftCardId: saleProduct.giftCardId,
      }

      return srp
    })

  let totalProducts = Decimal.zero()
  for (const prod of products) {
    totalProducts = totalProducts.add(prod.quantity.abs())
  }

  let productsPluralOrSingular = "products"
  if (totalProducts.eq(Decimal.fromBigSource(1))) {
    productsPluralOrSingular = "product"
  }

  const quantityString = `${totalProducts} ${productsPluralOrSingular}`

  return {
    products,
    quantityString,
  }
}

export function checkDiscountEquality(a?: Discount, b?: Discount) {
  if (!a && !b) {
    return true
  }
  if (!a || !b) {
    return false
  }
  if (a.type === "amount" && b.type === "amount") {
    return a.amount.eq(b.amount)
  }
  if (a.type === "rate" && b.type === "rate") {
    return a.rate.eq(b.rate)
  }
  return false
}
